import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { HeatMapDataType } from "@/pages/biReports/types";
import { GET_BI_DASHBOARD_BY_ID } from "@/pages/query/constants";
import { getBiDashboardById } from "@/helpers/apis";
import { RelativeComponentLoader } from "@/components/loader";
import { Stack } from "@altimateai/lego";
import { CopyButton } from "@/components/copyButton";
import { DashboardMetadata } from "./DashboardMetadata";
import { ColorInsightTags } from "@/components/tags";
import { Button, Card, CardContent, CardHeader } from "@altimateai/lego";
import { RegenerateIcon, Shines2Icon, UserGroupIcon } from "@assets/icons";
import { Heatmap } from "@/pages/biReports/biReportsDetailsComponents/Heatmap";
import { BiReportsOpportunities } from "@/pages/biReports/biReportsDetailsComponents/BiReportOpportunities";
import { useAppState } from "@/modules/app/useAppContext";
import { getInstanceIdByNameFromDataStores } from "@/helpers/utils";
import { ScrollContainer } from "@/components/scrollContainer";
import { ResourceNotFound } from "@/pages/biReports/errorPage/ResourceNotFound";
import BIUserReportInsightOrOpportunityTag, {
  insightToTagsTooltipDisplayFormat,
} from "@/pages/biReports/BIUserReportInsightOrOpportunityTag";
import BIReportTabs from "@/pages/biReports/BIReportTabs";
import { StreamingContent } from "@/modules/streaming";
import { BackButton } from "@/components/button";
import { useBreadcrumb, getEntityName } from "@/components/breadcrumbs";
import { useLocation } from "react-router-dom";
const createHeatMapMatrix = (matrix: number[][]): HeatMapDataType => {
  return {
    z: matrix,
    x: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    y: ["12am", "4am", "8am", "12pm", "4pm", "8pm"],
  };
};
export const BIDashboardDetailMainContent = ({
  setShowSideContent,
  setView,
  id,
  setSideContentViewsHeatmapData,
  setSideContentRefreshHeatmapData,
}: {
  id: string | undefined;
  setShowSideContent: React.Dispatch<React.SetStateAction<boolean>>;
  setView: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSideContentRefreshHeatmapData: (data: HeatMapDataType | null) => void;
  setSideContentViewsHeatmapData: (data: HeatMapDataType | null) => void;
}) => {
  const decodedRk = decodeURIComponent(id ?? "");
  const [viewsHeatmapData, setViewsHeatmapData] = useState<HeatMapDataType>(
    createHeatMapMatrix([])
  );
  const [refreshHeatmapData, setRefreshHeatmapData] = useState<HeatMapDataType>(
    createHeatMapMatrix([])
  );
  const { datastores } = useAppState();
  const { state } = useLocation();
  const { data: dashboardDetails, isFetching: isLoading } = useQuery({
    queryKey: [GET_BI_DASHBOARD_BY_ID, decodedRk],
    enabled: id !== undefined,
    queryFn: () => getBiDashboardById(decodedRk),
  });

  // Set breadcrumbs with the dashboard name - use location state as fallback for instant display
  useBreadcrumb(getEntityName(state) ?? dashboardDetails?.dashboard ?? "Loading...", {
    defaultParents: [
      { label: "Insights", pathname: "/bi-reports", noLink: true },
      { label: "Dashboards", pathname: "/bi-reports/dashboards" },
    ],
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (dashboardDetails) {
      setViewsHeatmapData(createHeatMapMatrix(dashboardDetails?.views_heatmap ?? []));
      setRefreshHeatmapData(createHeatMapMatrix(dashboardDetails?.refresh_heatmap ?? []));
    }
  }, [dashboardDetails]);
  if (isLoading) {
    return <RelativeComponentLoader label="Loading..." />;
  }
  if (!dashboardDetails) {
    return <ResourceNotFound variant={"dashboard"} />;
  }
  return (
    <ScrollContainer>
      <Stack className="mb-3">
        <BackButton defaultUrl="/bi-reports/dashboards" />
        <Stack direction="column" gap="lg" className="ms-2 me-2 al-w-full">
          <Stack align="center">
            <div className="fs-4 me-2">Dashboard Name:</div>
            <h2 className="fs-4 text-primary me-2">{dashboardDetails.dashboard}</h2>
            {dashboardDetails.dashboard_url && (
              <CopyButton value={dashboardDetails.dashboard_url} label="Copy URL" />
            )}
          </Stack>
          <DashboardMetadata dashboardDetails={dashboardDetails} />
          <Stack justify="start">
            <Stack justify="start" gap="md" wrap>
              {dashboardDetails.insights.map((insight, index) => (
                <BIUserReportInsightOrOpportunityTag
                  key={index}
                  insightOrOpportunity={insightToTagsTooltipDisplayFormat(insight)}
                />
              ))}
            </Stack>
            <div>
              <ColorInsightTags onClick={() => {}} tags={dashboardDetails?.tableauTags || []} />
            </div>
          </Stack>
          <div className={"al-min-h-56"}>
            <Card className={"!al-bg-[#eaf3ff] !al-border-action-brand"}>
              <CardHeader>
                <Stack gap={"md"} align={"center"}>
                  <Shines2Icon className="al-h-6 al-w-6" />
                  <h2 className="al-text-md">Summary</h2>
                </Stack>
                <p className="al-text-muted-foreground">
                  Key insights into dashboard usage patterns and data refresh activities over the
                  past 30 days
                </p>
              </CardHeader>
              <CardContent>
                <div className="al-p-4 rounded al-bg-white al-min-h-60 al-max-h-40 al-overflow-y-scroll">
                  <StreamingContent
                    id={`summary-${id}`}
                    endpoint={`/business_intelligence/dashboards/insights_summary_stream`}
                    requestBody={{ dashboard_id: id, view: "refreshes" }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
          <Stack
            direction="row"
            gap="lg"
            align="stretch"
            className="al-w-full al-flex-col lg:al-flex-row"
          >
            {dashboardDetails.views_heatmap && (
              <div className="al-w-full lg:al-w-1/2">
                <Card className="al-h-full">
                  <CardHeader>
                    <Stack align="center" gap="md">
                      <UserGroupIcon className="al-h-4 al-w-4 al-text-blue-500" />
                      <h2 className="al-text-md al-flex-1">Dashboard Views</h2>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Show Views Insights"
                        className="al-bg-muted hover:al-bg-muted/80"
                        onClick={() => {
                          setShowSideContent(true);
                          setView("views");
                          setSideContentViewsHeatmapData(viewsHeatmapData);
                        }}
                      >
                        <span className="al-text-xs al-font-medium al-text-blue-600">
                          Show Insights
                        </span>
                      </Button>
                    </Stack>
                  </CardHeader>
                  <CardContent>
                    <p className="al-text-xs al-text-muted-foreground al-my-2">
                      Cumulative dashboard views over 30 days by day of week and time of day.
                    </p>
                    <Stack justify="around">
                      <Heatmap
                        data={viewsHeatmapData}
                        colorscale="Blues"
                        ariaLabel="Dashboard views heatmap"
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </div>
            )}
            {dashboardDetails.refresh_heatmap && (
              <div className="al-w-full lg:al-w-1/2">
                <Card className="al-h-full">
                  <CardHeader>
                    <Stack align="center" gap="md">
                      <RegenerateIcon className="al-h-4 al-w-4 al-text-blue-500" />
                      <h2 className="al-text-md al-flex-1">Dashboard Refreshes</h2>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Show Refreshes Insights"
                        className="al-bg-muted hover:al-bg-muted/80"
                        onClick={() => {
                          setShowSideContent(true);
                          setView("refreshes");
                          setSideContentRefreshHeatmapData(refreshHeatmapData);
                        }}
                      >
                        <span className="al-text-xs al-font-medium al-text-blue-600">
                          Show Insights
                        </span>
                      </Button>
                    </Stack>
                  </CardHeader>
                  <CardContent>
                    <p className="al-text-xs al-text-muted-foreground al-my-2">
                      Cumulative Refreshes over 30 days by day of week and time of day.
                    </p>
                    <Stack justify="around">
                      <Heatmap
                        data={refreshHeatmapData}
                        colorscale="Blues"
                        ariaLabel="Dashboard refreshes heatmap"
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </div>
            )}
          </Stack>
          <div className="al-mt-1">
            <Card className="al-h-full">
              <CardContent className="!al-py-4">
                <BIReportTabs
                  has_lineage={dashboardDetails.has_lineage}
                  dashboardId={dashboardDetails.rk}
                  opportunities={
                    <BiReportsOpportunities
                      helperText={
                        "Optimization recommendations to improve dashboard performance and reduce resource usage"
                      }
                      instanceId={getInstanceIdByNameFromDataStores(
                        datastores,
                        dashboardDetails.instance_name
                      )}
                      rk={dashboardDetails.rk}
                    />
                  }
                />
              </CardContent>
            </Card>
          </div>
        </Stack>
      </Stack>
    </ScrollContainer>
  );
};
